import type {
  DeviceResourceMetadataDeviceV2Api,
  DeviceResourceReadV2Api,
} from '@ingenico/api-sdk-device-management-private-v2';
import {
  enumDeviceConnectionStatusV2,
  enumDeviceLogCapabilityStatusV2,
  enumDeviceMode,
  enumRemoteDesktopStatusV2,
} from '@ingenico/api-sdk-device-management-private-v2';
import { isEqual, isUndefined, toString } from 'lodash-es';
import { DeviceConnectionStatusType, DeviceMode, RemoteDesktopStatus } from '../../models';
import type { DeviceCallScheduling, DeviceInfo } from '../../models';
import { toIsoInstant, toIsoStringIfDate } from '../utils/date.utils';

export function computeConnectionStatus(
  rts: DeviceResourceReadV2Api['rts'],
): DeviceInfo['connection']['connectionStatus'] {
  if (isUndefined(rts)) {
    return undefined;
  }

  switch (rts.connectionStatus) {
    case enumDeviceConnectionStatusV2.CONNECTED: {
      return DeviceConnectionStatusType.Connected;
    }
    case enumDeviceConnectionStatusV2.DISCONNECTED: {
      return DeviceConnectionStatusType.Disconnected;
    }
    case enumDeviceConnectionStatusV2.NEVER_CONNECTED: {
      return DeviceConnectionStatusType.NeverConnected;
    }
    default: {
      return undefined;
    }
  }
}

export function computeConnection({ rts, id }: DeviceResourceReadV2Api): DeviceInfo['connection'] {
  const connectionStatus = computeConnectionStatus(rts);
  return {
    connectionStatus,
    isConnected: isEqual(connectionStatus, DeviceConnectionStatusType.Connected),
    timestamp: toIsoStringIfDate(rts?.lastConnectionDate),
    id: toString(id),
  };
}
function isUndefinedOrString(text?: string): string | undefined {
  return isUndefined(text) ? undefined : toString(text);
}

export function computeBaseInfo({ metadata: { device, system } }: DeviceResourceReadV2Api): DeviceInfo['baseInfo'] {
  return {
    androidVersion: isUndefinedOrString(system?.androidVersion),
    deviceFamily: isUndefinedOrString(device?.family),
    firmware: isUndefinedOrString(system?.osVersion),
    manufacturerPartNumber: isUndefinedOrString(device?.partNumber),
    mode: computeBaseInfoMode(device),
    serialNumber: isUndefinedOrString(device?.serialNumber),
  };
}

function computeBaseInfoMode(device?: DeviceResourceMetadataDeviceV2Api): DeviceMode | undefined {
  if (isUndefined(device) || isUndefined(device.mode)) {
    return undefined;
  }
  return isEqual(device.mode, enumDeviceMode.MOCKUP) ? DeviceMode.Dev : DeviceMode.Production;
}

function computeCallScheduling({ metadata: { device } }: DeviceResourceReadV2Api): DeviceCallScheduling | undefined {
  const callScheduling = device?.callScheduling;

  if (isUndefined(callScheduling)) {
    return undefined;
  }

  return {
    callFrequency: callScheduling.callFrequency,
    endTimeWindow: callScheduling.endTimeWindow,
    nextCallDate: toIsoInstant(callScheduling.nextCallDate),
    retryPeriod: callScheduling.retryPeriod,
    startTimeWindow: callScheduling.startTimeWindow,
  };
}

export function mapDeviceResourceReadV2Api(deviceResponse: DeviceResourceReadV2Api, deviceId: string): DeviceInfo {
  return {
    baseInfo: computeBaseInfo(deviceResponse),
    connection: computeConnection(deviceResponse),
    callScheduling: computeCallScheduling(deviceResponse),
    isLogcatReady: deviceResponse.rts?.deviceLogCapabilityStatus === enumDeviceLogCapabilityStatusV2.READY,
    remoteDesktopStatus:
      deviceResponse.rts?.remoteDesktopStatus === enumRemoteDesktopStatusV2.READY
        ? RemoteDesktopStatus.Ready
        : RemoteDesktopStatus.Unavailable,
    isRtsEnabled: deviceResponse.isRtsEnabled,
    id: deviceId,
    connectionStatus: deviceResponse.rts?.connectionStatus,
    screenLockingStatus: deviceResponse.metadata?.device?.screenLockingStatus,
  };
}
